import { useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import { RemindingTone } from '../../../../api/gql';
import { invoiceValidationSchema } from '../../../../utils/validationSchemas';
import { ConnectedInvoiceForm } from '../connected/ConnectedInvoiceForm';
import { InvoiceFormType } from '../types';

export type InvoiceFormProviderProps = {
  readonly?: boolean;
  onSubmit: (data: InvoiceFormType) => void;
  defaultValues?: Partial<InvoiceFormType>;
  defaultValidated?: boolean;
  highlightAreas?: boolean;
  formId?: string;
  allowInvoiceItems?: boolean;
};

export const InvoiceFormProvider = ({
  readonly,
  onSubmit,
  defaultValues = {},
  defaultValidated,
  highlightAreas = true,
  allowInvoiceItems = true,
  formId,
}: InvoiceFormProviderProps) => {
  const { trigger, reset, ...formMethods } = useForm({
    defaultValues: { ...defaultEmptyValues, ...defaultValues },
    resolver: yupResolver(invoiceValidationSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  // used to trigger validation on startup
  useEffect(() => {
    if (defaultValidated) {
      trigger();
    }
  }, [defaultValidated, trigger]);

  // used to update data in readonly mode
  useEffect(() => {
    if (readonly) {
      reset({ ...defaultEmptyValues, ...defaultValues });
    }
  }, [readonly, defaultValues, reset]);

  return (
    <FormProvider {...formMethods} trigger={trigger} reset={reset}>
      <ConnectedInvoiceForm
        onSubmit={onSubmit}
        highlightAreas={highlightAreas}
        readonly={readonly}
        formId={formId}
        allowInvoiceItems={allowInvoiceItems}
      />
    </FormProvider>
  );
};

const defaultEmptyValues: InvoiceFormType = {
  number: '',
  amount: null,
  currency: 'czk',
  amountDue: null,
  issueDate: '',
  dueDate: '',
  bankAccount: '',
  bankNumber: '',
  iban: '',
  swift: '',
  variableSymbol: '',
  customer: {
    name: '',
    address: '',
    email: '',
    phoneNumber: '',
    taxId: '',
    vatId: '',
    remindingTone: RemindingTone.Neutral,
  },
  vendor: {
    name: '',
    address: '',
    taxId: '',
    vatId: '',
  },
};
